import type { SignInErrorReason } from '@/lib/services/auth';
import type { PublicPropertyFilterState } from '@/lib/types/publicProperty';
import type { PropertyListFilters, UnitAvailabilityStatus } from '@/lib/types/property';

interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

type DataLayer = Array<Record<string, unknown>>;

function pushToDataLayer(payload: DataLayerEvent): void {
  if (typeof window === 'undefined') {
    return;
  }

  const globalWindow = window as typeof window & { dataLayer?: DataLayer };
  let dataLayer = globalWindow.dataLayer;

  if (!Array.isArray(dataLayer)) {
    if (import.meta.env.MODE !== 'production') {
      dataLayer = [];
      globalWindow.dataLayer = dataLayer;
    } else {
      return;
    }
  }

  dataLayer.push(payload);
}

export function trackLeadSubmission(params: {
  leadId: string;
  campaignSource: string | null;
  campaignMedium: string | null;
  stage?: string;
}): void {
  pushToDataLayer({
    event: 'lead_submission',
    leadId: params.leadId,
    campaignSource: params.campaignSource,
    campaignMedium: params.campaignMedium,
    stage: params.stage ?? 'new',
  });
}

export function trackLeadStageChange(params: {
  leadId: string;
  stage: string;
  previousStage: string | null;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'lead_stage_change',
    leadId: params.leadId,
    stage: params.stage,
    previousStage: params.previousStage,
    origin: params.origin ?? 'dashboard',
  });
}

export function trackLeadFollowUpScheduled(params: {
  leadId: string;
  nextFollowUpAt: string | null;
  previousNextFollowUpAt: string | null;
  sourceAction: 'schedule' | 'clear';
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'lead_follow_up',
    leadId: params.leadId,
    nextFollowUpAt: params.nextFollowUpAt,
    previousNextFollowUpAt: params.previousNextFollowUpAt,
    sourceAction: params.sourceAction,
    origin: params.origin ?? 'dashboard',
  });
}

export function trackLeadContactLogged(params: {
  leadId: string;
  contactedAt: string | null;
  nextFollowUpAt: string | null;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'lead_contact_logged',
    leadId: params.leadId,
    contactedAt: params.contactedAt,
    nextFollowUpAt: params.nextFollowUpAt,
    origin: params.origin ?? 'dashboard',
  });
}

export function trackAuthSignInFailure(params: {
  reason: SignInErrorReason;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'auth_sign_in_failed',
    reason: params.reason,
    origin: params.origin ?? 'auth_dialog',
  });
}

export function trackPropertyCreateSuccess(params: {
  propertyId: string;
  assetType: string;
  submarket: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_create_success',
    propertyId: params.propertyId,
    assetType: params.assetType,
    submarket: params.submarket,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyCreateFailure(params: {
  propertyCode?: string;
  errorName: string;
  errorMessage?: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_create_failed',
    propertyCode: params.propertyCode,
    errorName: params.errorName,
    errorMessage: params.errorMessage,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyUpdateSuccess(params: { propertyId: string; origin?: string }): void {
  pushToDataLayer({
    event: 'property_update_success',
    propertyId: params.propertyId,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyUpdateFailure(params: {
  propertyId: string;
  errorName: string;
  errorMessage?: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_update_failed',
    propertyId: params.propertyId,
    errorName: params.errorName,
    errorMessage: params.errorMessage,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyListSwipeAction(params: {
  propertyId: string;
  action: 'archive' | 'share' | 'open';
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_list_swipe_action',
    propertyId: params.propertyId,
    action: params.action,
    origin: params.origin ?? 'properties_mobile_list',
  });
}

export function trackPropertyActionSheet(params: {
  propertyId: string;
  selection: 'view' | 'share' | 'archive';
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_action_sheet',
    propertyId: params.propertyId,
    selection: params.selection,
    origin: params.origin ?? 'properties_mobile_sheet',
  });
}

export function trackPublicPropertyFilterChange(state: PublicPropertyFilterState): void {
  pushToDataLayer({
    event: 'public.properties.filter',
    submarkets: state.submarkets,
    assetTypes: state.assetTypes,
    rentableMin: state.rentableSquareFeet.min,
    rentableMax: state.rentableSquareFeet.max,
    rentMin: state.rentPerSquareFoot.min,
    rentMax: state.rentPerSquareFoot.max,
    incentivesOnly: state.incentivesOnly,
    availabilityStart: state.availability.start,
    availabilityEnd: state.availability.end,
    readinessStates: state.readinessStates,
    potentiallyDivisible: state.potentiallyDivisible,
  });
}

export function trackDashboardPropertyFilterChange(filters: PropertyListFilters): void {
  pushToDataLayer({
    event: 'dashboard.properties.filter',
    submarkets: filters.submarkets ?? [],
    assetTypes: filters.assetTypes ?? [],
    rentableMin: filters.rentableSquareFeet?.min ?? null,
    rentableMax: filters.rentableSquareFeet?.max ?? null,
    rentMin: filters.rentPerSquareFoot?.min ?? null,
    rentMax: filters.rentPerSquareFoot?.max ?? null,
    readiness: filters.readiness ?? [],
    incentivesOnly: filters.incentivesOnly ?? false,
    potentiallyDivisible:
      typeof filters.potentiallyDivisible === 'boolean' ? filters.potentiallyDivisible : null,
    hasVacancy: filters.hasVacancy ?? false,
  });
}

export function trackUnitArchive(params: {
  unitId: string;
  propertyId: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'unit_archive',
    unitId: params.unitId,
    propertyId: params.propertyId,
    origin: params.origin ?? 'units_table',
  });
}

export function trackUnitDelete(params: {
  unitId: string;
  propertyId: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'unit_delete',
    unitId: params.unitId,
    propertyId: params.propertyId,
    origin: params.origin ?? 'units_table',
  });
}

export function trackMobileFilterDrawerEvent(params: {
  drawerId: string;
  action: 'open' | 'close' | 'apply' | 'reset';
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'mobile_filter_drawer',
    drawerId: params.drawerId,
    action: params.action,
    origin: params.origin ?? 'properties_mobile_filters',
  });
}

export function trackPropertyArchiveSuccess(params: { propertyId: string; origin?: string }): void {
  pushToDataLayer({
    event: 'property_archive_success',
    propertyId: params.propertyId,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyArchiveFailure(params: {
  propertyId: string;
  errorName: string;
  errorMessage?: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_archive_failed',
    propertyId: params.propertyId,
    errorName: params.errorName,
    errorMessage: params.errorMessage,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyRestoreSuccess(params: { propertyId: string; origin?: string }): void {
  pushToDataLayer({
    event: 'property_restore_success',
    propertyId: params.propertyId,
    origin: params.origin ?? 'property_form',
  });
}

export function trackPropertyRestoreFailure(params: {
  propertyId: string;
  errorName: string;
  errorMessage?: string;
  origin?: string;
}): void {
  pushToDataLayer({
    event: 'property_restore_failed',
    propertyId: params.propertyId,
    errorName: params.errorName,
    errorMessage: params.errorMessage,
    origin: params.origin ?? 'property_form',
  });
}

type PropertyQuickActionType = 'marketing_summary_edit';

export function trackPropertyQuickAction(params: {
  propertyId: string;
  propertyCode?: string;
  action: PropertyQuickActionType;
  status: 'success' | 'error';
  origin?: string;
  errorMessage?: string;
  bulk?: boolean;
}): void {
  pushToDataLayer({
    event: 'dashboard.property.quick_action',
    propertyId: params.propertyId,
    propertyCode: params.propertyCode ?? null,
    action: params.action,
    status: params.status,
    origin: params.origin ?? 'dashboard',
    errorMessage: params.errorMessage ?? null,
    bulk: params.bulk ?? false,
  });
}

type PublicPropertyActionType =
  | 'unit_view'
  | 'unit_cta_click'
  | 'unit_cta_blocked'
  | 'unit_favorite_add'
  | 'unit_favorite_remove'
  | 'unit_showing_request'
  | 'unit_showing_start'
  | 'unit_showing_error'
  | 'unit_showing_register_link'
  | 'unit_portal_invite_queued'
  | 'unit_jump'
  | 'map_view'
  | 'directions_click'
  | 'floor_plan_view'
  | 'blank_canvas_ack'
  | 'blank_canvas_cta_click';

export function trackPublicPropertyAction(params: {
  propertyId: string;
  propertyCode: string;
  action: PublicPropertyActionType;
  unitId?: string | null;
  readiness?: UnitAvailabilityStatus | null;
  reason?: string | null;
  viewport?: 'desktop' | 'tablet' | 'mobile';
  detail?: Record<string, unknown> | null;
}): void {
  pushToDataLayer({
    event: 'public.property.action',
    propertyId: params.propertyId,
    propertyCode: params.propertyCode,
    action: params.action,
    unitId: params.unitId ?? null,
    readiness: params.readiness ?? null,
    reason: params.reason ?? null,
    viewport: params.viewport ?? detectViewportSegment(),
    detail: params.detail ?? null,
  });
}

function detectViewportSegment(): 'desktop' | 'tablet' | 'mobile' {
  if (typeof window === 'undefined') {
    return 'desktop';
  }
  const width = window.innerWidth;
  if (width >= 1280) {
    return 'desktop';
  }
  if (width >= 768) {
    return 'tablet';
  }
  return 'mobile';
}
